import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth, can } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/format";
import {
  Gauge, Wrench, Users, Car, CalendarBlank, ListChecks, Package,
  CurrencyDollar, UserGear, SignOut, List as ListIcon, X, Gear,
} from "@phosphor-icons/react";
import CurrencyTicker from "@/components/CurrencyTicker";
import { useSettings } from "@/context/SettingsContext";

const BRAND = "var(--brand)";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NAV = [
  { to: "/orders", module: "orders", label: "Замовлення-наряди", icon: Wrench },
  { to: "/appointments", module: "appointments", label: "Календар / запис", icon: CalendarBlank },
  { to: "/clients", module: "clients", label: "Клієнти", icon: Users },
  { to: "/vehicles", module: "vehicles", label: "Автомобілі", icon: Car },
  { to: "/services", module: "services", label: "Послуги та прайс", icon: ListChecks },
  { to: "/inventory", module: "inventory", label: "Склад", icon: Package },
  { to: "/", module: "dashboard", label: "Дашборд", icon: Gauge, end: true },
  { to: "/finance", module: "finance", label: "Фінанси", icon: CurrencyDollar },
  { to: "/users", module: "users", label: "Персонал і ролі", icon: UserGear },
  { to: "/settings", module: "settings", label: "Налаштування", icon: Gear },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { logoSrc } = useSettings();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const items = NAV.filter((n) => can(user, n.module));

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: BRAND }}>
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 h-full w-64 flex flex-col transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        style={{ background: BRAND }}
        data-testid="app-sidebar"
      >
        <div className="h-16 flex items-center justify-center px-5 relative">
          <img src={logoSrc || "/viwat-logo.svg"} alt="VIWAT" className="h-11 max-w-[180px] w-auto object-contain brightness-0 invert" />
          <button className="absolute right-4 lg:hidden text-white" onClick={() => setOpen(false)} data-testid="sidebar-close">
            <X size={22} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-1">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.module}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-white/15 text-white font-semibold shadow-inner" : "text-white/75 hover:bg-white/10 hover:text-white font-medium"}`
              }
            >
              <n.icon size={20} weight="regular" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3">
          <button onClick={() => { navigate("/profile"); setOpen(false); }} data-testid="profile-link"
            className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/10 transition-colors text-left">
            <div className="w-9 h-9 rounded-full bg-white/15 text-white flex items-center justify-center font-head font-bold text-sm overflow-hidden shrink-0">
              {user.avatar
                ? <img src={`${BACKEND_URL}/api/files/${user.avatar}?w=90`} alt="" className="w-full h-full object-cover" />
                : (user.name?.[0] || "?")}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate text-white">{user.name}</div>
              <div className="text-xs text-white/60 truncate">{ROLE_LABELS[user.role]}</div>
            </div>
          </button>
          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            data-testid="logout-btn"
            className="mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/75 hover:bg-white/10 hover:text-white transition-colors"
          >
            <SignOut size={18} /> Вийти
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 flex items-center px-4 lg:px-6 gap-3 shrink-0 text-white">
          <button className="lg:hidden text-white" onClick={() => setOpen(true)} data-testid="sidebar-open">
            <ListIcon size={24} />
          </button>
          <CurrencyTicker />
        </header>
        <div className="flex-1 min-h-0">
          <main
            className="bg-background text-foreground rounded-tl-2xl shadow-xl h-full overflow-y-auto p-4 lg:p-8"
            data-testid="main-content"
          >
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
